import {
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'

import {
  fetchApartmentOverview,
  fetchApartments,
  fetchClusters,
  fetchGrowthDashboard
} from 'api/apartment'
import { Loading } from 'app/components/generic/LottieComponents'
import { formatAmount } from 'app/utils'

import ApartmentPageHeader from '../shared/ApartmentPageHeader'
import MonthFilter from '../shared/MonthFilter'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), minWidth: 0, overflowX: 'hidden' },
  metricCard: { padding: theme.spacing(2.5), height: '100%' },
  metricValue: { fontWeight: 600, marginTop: theme.spacing(0.5) },
  sectionTitle: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1.5),
    fontWeight: 600
  },
  filter: { minWidth: 200 }
}))

const Metric = ({ label, value, classes }) => (
  <Paper variant="outlined" className={classes.metricCard}>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="h4" className={classes.metricValue}>
      {value}
    </Typography>
  </Paper>
)

const pct = (v) => (v == null ? '—' : `${v}%`)

// 'YYYY-MM' -> "Jul '26". Self-contained: no dependency on a loaded options list.
const monthLabelOf = (value) => {
  if (!value) return 'All months'
  const [y, m] = String(value).split('-')
  const d = new Date(Number(y), Number(m) - 1, 1)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('en-IN', { month: 'short', year: '2-digit' })
}
// The API sends a fraction (0.7669); the sheet reads it as a percentage.
const ratio = (v) => (v == null ? '—' : `${Math.round(v * 1000) / 10}%`)

// The overview filter defaults to "all apartments" and remembers the last
// choice across reloads (matches the shared apartment selector's persistence).
const OVERVIEW_FILTER_KEY = 'apartment.overview.filter'
const OVERVIEW_MONTH_KEY = 'apartment.overview.month'

// Growth is reported for whichever month is selected. Re-enrolment is
// month-over-month: of the parents who paid for the previous month, the share
// who paid for this one.
// Sums the columns that can be summed. Re-enrolment % is NOT summed — it is
// recomputed from the totals (74/113), because averaging percentages across
// buildings of different sizes gives a number that means nothing.
const totalsOf = (rows) => {
  const t = rows.reduce(
    (acc, r) => ({
      enrolledParents: acc.enrolledParents + (r.enrolledParents || 0),
      enrolments: acc.enrolments + (r.enrolments || 0),
      reCount: acc.reCount + (r.reEnrolment?.count || 0),
      reBase: acc.reBase + (r.reEnrolment?.base || 0),
      newEnrolments: acc.newEnrolments + (r.newEnrolments || 0),
      winBack: acc.winBack + (r.winBack || 0),
      revenue: acc.revenue + (r.revenue || 0)
    }),
    {
      enrolledParents: 0,
      enrolments: 0,
      reCount: 0,
      reBase: 0,
      newEnrolments: 0,
      winBack: 0,
      revenue: 0
    }
  )
  return {
    ...t,
    rePct: t.reBase ? t.reCount / t.reBase : null,
    aov: t.enrolledParents ? t.revenue / t.enrolledParents : 0
  }
}

const TotalsRow = ({ rows }) => {
  const t = totalsOf(rows)
  const bold = { fontWeight: 600 }
  return (
    <TableRow>
      <TableCell style={bold}>Total</TableCell>
      <TableCell />
      <TableCell align="right" style={bold}>
        {t.enrolledParents}
      </TableCell>
      <TableCell align="right" style={bold}>
        {t.enrolments}
      </TableCell>
      <TableCell align="right" style={bold}>
        {t.reCount}
        {t.reBase ? ` / ${t.reBase}` : ''}
      </TableCell>
      <TableCell align="right" style={bold}>
        {ratio(t.rePct)}
      </TableCell>
      <TableCell align="right" style={bold}>
        {t.newEnrolments}
      </TableCell>
      <TableCell align="right" style={bold}>
        {t.winBack}
      </TableCell>
      <TableCell align="right" style={bold}>
        {formatAmount(t.revenue)}
      </TableCell>
      <TableCell align="right" style={bold}>
        {formatAmount(t.aov)}
      </TableCell>
    </TableRow>
  )
}

// Totals for the "By apartment" table. Conversion and collection % are
// RECOMPUTED from the totals, never averaged across buildings — averaging a
// percentage over buildings of different sizes produces a meaningless number.
const PerApartmentTotals = ({ rows }) => {
  const t = rows.reduce(
    (a, r) => ({
      enrolments: a.enrolments + (r.enrolments || 0),
      parents: a.parents + (r.parents || 0),
      trials: a.trials + (r.trials || 0),
      collected: a.collected + (r.collected || 0),
      outstanding: a.outstanding + (r.outstanding || 0)
    }),
    { enrolments: 0, parents: 0, trials: 0, collected: 0, outstanding: 0 }
  )
  const billed = t.collected + t.outstanding
  const bold = { fontWeight: 600 }
  return (
    <TableRow>
      <TableCell style={bold}>Total</TableCell>
      <TableCell />
      <TableCell align="right" style={bold}>
        {t.enrolments}
      </TableCell>
      <TableCell align="right" style={bold}>
        {t.parents}
      </TableCell>
      <TableCell align="right" style={bold}>
        {t.trials}
      </TableCell>
      <TableCell align="right" style={bold}>
        —
      </TableCell>
      <TableCell align="right" style={bold}>
        {formatAmount(t.collected)}
      </TableCell>
      <TableCell align="right" style={bold}>
        {formatAmount(t.outstanding)}
      </TableCell>
      <TableCell align="right" style={bold}>
        {billed ? `${Math.round((t.collected / billed) * 1000) / 10}%` : '—'}
      </TableCell>
    </TableRow>
  )
}

const GrowthRow = ({ r, classes }) => (
  <TableRow key={r.slug}>
    <TableCell>{r.name}</TableCell>
    <TableCell>{r.cluster || '—'}</TableCell>
    <TableCell align="right">{r.enrolledParents}</TableCell>
    <TableCell align="right">{r.enrolments}</TableCell>
    <TableCell align="right">
      {r.reEnrolment.count}
      {r.reEnrolment.base ? ` / ${r.reEnrolment.base}` : ''}
    </TableCell>
    <TableCell align="right">{ratio(r.reEnrolment.pct)}</TableCell>
    <TableCell align="right">{r.newEnrolments}</TableCell>
    <TableCell align="right">{r.winBack}</TableCell>
    <TableCell align="right">{formatAmount(r.revenue)}</TableCell>
    <TableCell align="right">{formatAmount(r.aov)}</TableCell>
  </TableRow>
)

const ApartmentOverview = () => {
  const classes = useStyles()
  const [apartments, setApartments] = React.useState([])
  const [apartment, setApartmentState] = React.useState(() =>
    typeof localStorage !== 'undefined'
      ? localStorage.getItem(OVERVIEW_FILTER_KEY) || ''
      : ''
  )
  const setApartment = (value) => {
    setApartmentState(value)
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(OVERVIEW_FILTER_KEY, value)
    }
  }
  const [data, setData] = React.useState(null)
  // '' until the real months load; MonthFilter selects the newest that exists.
  const [month, setMonthState] = React.useState(
    () =>
      (typeof localStorage !== 'undefined' &&
        localStorage.getItem(OVERVIEW_MONTH_KEY)) ||
      ''
  )
  const setMonth = (value) => {
    setMonthState(value)
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(OVERVIEW_MONTH_KEY, value)
    }
  }
  const [growth, setGrowth] = React.useState(null)
  const [clusters, setClusters] = React.useState([])
  // With 20+ buildings coming, an org-wide roll-up stops being a useful default.
  const [cluster, setCluster] = React.useState('')

  React.useEffect(() => {
    fetchApartments()
      .then(setApartments)
      .catch(() => {})
    fetchClusters()
      .then(setClusters)
      .catch(() => setClusters([]))
  }, [])

  React.useEffect(() => {
    let active = true
    setData(null)
    // The month picker drives the totals too, not just the growth section —
    // otherwise Collected/Outstanding sit directly above a month's growth
    // figures while silently reporting all time.
    fetchApartmentOverview(apartment, month, cluster)
      .then((res) => active && setData(res))
      .catch(
        () => active && setData({ totals: {}, alerts: {}, perApartment: [] })
      )
    return () => {
      active = false
    }
  }, [apartment, month, cluster])

  React.useEffect(() => {
    let active = true
    setGrowth(null)
    fetchGrowthDashboard(month, cluster)
      .then((res) => active && setGrowth(res))
      .catch(() => active && setGrowth({ apartments: [], clusters: [] }))
    return () => {
      active = false
    }
  }, [month, cluster])

  const filters = (
    <div style={{ display: 'flex', gap: 8 }}>
      <MonthFilter value={month} onChange={setMonth} apartmentId={apartment} />
      <FormControl variant="outlined" size="small" className={classes.filter}>
        <Select
          value={cluster}
          displayEmpty
          onChange={(e) => setCluster(e.target.value)}
        >
          <MenuItem value="">All clusters</MenuItem>
          {clusters.map((c) => (
            <MenuItem key={c.slug} value={c.slug}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl variant="outlined" size="small" className={classes.filter}>
        <Select
          value={apartment}
          // Without displayEmpty, MUI renders a Select whose value is '' as a
          // BLANK BOX — so "All apartments" looked like nothing was selected.
          displayEmpty
          onChange={(e) => setApartment(e.target.value)}
        >
          <MenuItem value="">All apartments</MenuItem>
          {apartments.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  )

  if (!data) return <Loading />

  const t = data.totals || {}
  const metrics = [
    { label: 'Apartments', value: t.apartments ?? 0 },
    { label: 'Active enrolments', value: t.activeEnrolments ?? 0 },
    { label: 'Parents', value: t.parents ?? 0 },
    { label: 'Collection rate', value: pct(t.collectionRate) }
  ]
  const money = [
    { label: 'Collected', value: formatAmount(t.collected || 0) },
    { label: 'Outstanding', value: formatAmount(t.outstanding || 0) },
    { label: 'Open incidents', value: data.alerts?.openIncidents || 0 }
  ]

  const g = growth?.total
  // Derived from the value, NOT from an options array. The options now live
  // inside MonthFilter (fetched from the backend), and reaching for a `months`
  // variable that no longer exists in this scope is what crashed this page.
  const monthLabel = monthLabelOf(month)
  const growthTiles = g
    ? [
        { label: `Enrolled parents (${monthLabel})`, value: g.enrolledParents },
        { label: `Enrolments (${monthLabel})`, value: g.enrolments },
        { label: 'Re-enrolment', value: ratio(g.reEnrolment.pct) },
        { label: 'New enrolments', value: g.newEnrolments },
        { label: 'Win-back', value: g.winBack },
        { label: `Revenue (${monthLabel})`, value: formatAmount(g.revenue) },
        { label: 'Revenue / parent', value: formatAmount(g.aov) }
      ]
    : []

  return (
    <div className={classes.root}>
      <ApartmentPageHeader
        title="Overview"
        showSelector={false}
        subtitle={apartment ? 'Single apartment' : 'All apartments'}
        action={filters}
      />

      <Grid container spacing={2}>
        {metrics.map((m) => (
          <Grid item xs={6} md={3} key={m.label}>
            <Metric label={m.label} value={m.value} classes={classes} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} style={{ marginTop: 0 }}>
        {money.map((m) => (
          <Grid item xs={6} md={3} key={m.label}>
            <Metric label={m.label} value={m.value} classes={classes} />
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" className={classes.sectionTitle}>
        Growth — {monthLabel}
      </Typography>
      {!growth ? (
        <Loading />
      ) : (
        <>
          <Grid container spacing={2}>
            {growthTiles.map((m) => (
              <Grid item xs={6} md={3} key={m.label}>
                <Metric label={m.label} value={m.value} classes={classes} />
              </Grid>
            ))}
          </Grid>

          {g?.unattributed?.count ? (
            <Typography
              variant="caption"
              color="textSecondary"
              component="div"
              style={{ marginTop: 8 }}
            >
              {g.unattributed.count} payment
              {g.unattributed.count === 1 ? '' : 's'} (
              {formatAmount(g.unattributed.amount)}) could not be matched to a
              family, so they count towards revenue but not towards the parent
              cohorts above.
            </Typography>
          ) : null}

          <Typography variant="h6" className={classes.sectionTitle}>
            By cluster
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cluster</TableCell>
                  <TableCell />
                  <TableCell align="right">Parents</TableCell>
                  <TableCell align="right">Enrolments</TableCell>
                  <TableCell align="right">Re-enrolled</TableCell>
                  <TableCell align="right">Re-enrolment %</TableCell>
                  <TableCell align="right">New</TableCell>
                  <TableCell align="right">Win-back</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Rev / parent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {growth.clusters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No clusters configured
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {growth.clusters.map((r) => (
                      <GrowthRow key={r.slug} r={r} classes={classes} />
                    ))}
                    <TotalsRow rows={growth.clusters} />
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" className={classes.sectionTitle}>
            Growth by apartment
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Apartment</TableCell>
                  <TableCell>Cluster</TableCell>
                  <TableCell align="right">Parents</TableCell>
                  <TableCell align="right">Enrolments</TableCell>
                  <TableCell align="right">Re-enrolled</TableCell>
                  <TableCell align="right">Re-enrolment %</TableCell>
                  <TableCell align="right">New</TableCell>
                  <TableCell align="right">Win-back</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Rev / parent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {growth.apartments.map((r) => (
                  <GrowthRow key={r.slug} r={r} classes={classes} />
                ))}
                <TotalsRow rows={growth.apartments} />
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Typography variant="h6" className={classes.sectionTitle}>
        By apartment
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Apartment</TableCell>
              <TableCell>Cluster</TableCell>
              <TableCell align="right">Enrolments</TableCell>
              <TableCell align="right">Parents</TableCell>
              <TableCell align="right">Trials</TableCell>
              <TableCell align="right">Conversion</TableCell>
              <TableCell align="right">Collected</TableCell>
              <TableCell align="right">Outstanding</TableCell>
              <TableCell align="right">Collection %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.perApartment.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No apartments in this view
                </TableCell>
              </TableRow>
            ) : (
              data.perApartment.map((r) => (
                <TableRow key={r.slug}>
                  <TableCell>{r.apartment}</TableCell>
                  <TableCell>{r.cluster || '—'}</TableCell>
                  <TableCell align="right">{r.enrolments}</TableCell>
                  <TableCell align="right">{r.parents}</TableCell>
                  <TableCell align="right">{r.trials}</TableCell>
                  <TableCell align="right">
                    {pct(r.trialConversionRate)}
                  </TableCell>
                  <TableCell align="right">
                    {formatAmount(r.collected)}
                  </TableCell>
                  <TableCell align="right">
                    {formatAmount(r.outstanding)}
                  </TableCell>
                  <TableCell align="right">{pct(r.collectionRate)}</TableCell>
                </TableRow>
              ))
            )}
            {data.perApartment.length > 0 && (
              <PerApartmentTotals rows={data.perApartment} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default ApartmentOverview
